const COMPANY_CACHE_KEY = 'company_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

class CompanyCache {
  constructor() {
    this.cache = this.loadFromStorage();
  }

  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(COMPANY_CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if cache is expired
        if (Date.now() - parsed.timestamp < CACHE_DURATION) {
          return parsed.data;
        } else {
          // Cache expired, clear it
          sessionStorage.removeItem(COMPANY_CACHE_KEY);
        }
      }
      return {};
    } catch (error) {
      console.error('Error loading company cache:', error);
      return {};
    }
  }

  saveToStorage() {
    try {
      const cacheData = {
        data: this.cache,
        timestamp: Date.now()
      };
      sessionStorage.setItem(COMPANY_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving company cache:', error);
    }
  }

  get(uuid) {
    return this.cache[uuid] || null;
  }

  getMultiple(uuids) {
    const results = {};
    const missing = [];

    uuids.forEach(uuid => {
      if (this.cache[uuid]) {
        results[uuid] = this.cache[uuid];
      } else {
        missing.push(uuid);
      }
    });

    return { found: results, missing };
  }

  set(uuid, companyData) {
    // Store only essential fields to reduce memory usage
    this.cache[uuid] = {
      uuid: companyData.uuid,
      name: companyData.name,
      client_name: companyData.client?.name || 'Unknown Client',
      client_uuid: companyData.client?.uuid
    };
    this.saveToStorage();
  }

  setMultiple(companies) {
    companies.forEach(company => {
      // Store only essential fields to reduce memory usage
      this.cache[company.uuid] = {
        uuid: company.uuid,
        name: company.name,
        client_name: company.client?.name || 'Unknown Client',
        client_uuid: company.client?.uuid
      };
    });
    this.saveToStorage();
  }

  clear() {
    this.cache = {};
    sessionStorage.removeItem(COMPANY_CACHE_KEY);
  }

  isExpired() {
    try {
      const stored = sessionStorage.getItem(COMPANY_CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Date.now() - parsed.timestamp >= CACHE_DURATION;
      }
      return true;
    } catch (error) {
      return true;
    }
  }

  getCacheStats() {
    const count = Object.keys(this.cache).length;
    const stored = sessionStorage.getItem(COMPANY_CACHE_KEY);
    let age = 0;
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        age = Date.now() - parsed.timestamp;
      } catch (error) {
        // ignore
      }
    }
    return { count, age, expired: this.isExpired() };
  }
}

// Create singleton instance
const companyCache = new CompanyCache();

export default companyCache;
